"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import { useEffect, useRef } from "react";
import { CopyButton } from "@/components/ui/CopyButton";
import { useFocusTrap } from "@/hooks/useFocusTrap";
import { useShareModal, useShareUrl } from "../useShareUrl";
import { ShareButtons } from "./ShareButtons";

/**
 * Share the current view.
 *
 * The URL carries the camera, the selected POI and the active view, so what's
 * shared is what the sender is looking at — which is why it's read when the dialog
 * opens rather than once.
 */
export function ShareModal() {
  const t = useTranslations();
  const { isOpen, close } = useShareModal();
  const { url, embedCode } = useShareUrl(isOpen);

  const dialogRef = useRef<HTMLDivElement>(null);
  const restoreFocusTo = useRef<HTMLElement | null>(null);

  useFocusTrap(dialogRef, isOpen, close);

  useEffect(() => {
    if (!isOpen) return;

    // Focus has to come back where it started, or a keyboard user is dropped at
    // the top of the document when the dialog closes.
    restoreFocusTo.current = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();

    return () => restoreFocusTo.current?.focus();
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed left-0 top-0 z-50 flex h-full w-full items-center justify-center bg-primary-dark-75"
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={t("poiPage.share")}
      tabIndex={-1}
    >
      <div className="flex w-full max-w-lg flex-col bg-white p-9 dark:bg-dark-color">
        <div className="flex justify-between">
          <span className="text-base font-semibold">{t("poiPage.share")}</span>
          <button
            type="button"
            onClick={close}
            aria-label={t("modal.share.buttons.close")}
            className="focus-element flex justify-end"
          >
            <Image
              src="/icons/close.svg"
              alt=""
              width={12}
              height={12}
              className="dark:invert dark:filter"
            />
          </button>
        </div>

        <ShareButtons url={url} />

        <p className="mb-0 mt-2 text-xs font-semibold leading-relaxed">
          {t("modal.share.embed")}
        </p>
        <textarea
          className="w-full resize-none rounded-sm border-0 bg-transparent p-0 text-xs leading-relaxed text-black focus:outline-none focus:ring-gray-100 dark:text-off-white dark:focus:ring-gray-800"
          cols={45}
          rows={5}
          readOnly
          value={embedCode}
        />
        <div className="mt-1 flex justify-end">
          <CopyButton content={embedCode} />
        </div>
      </div>
    </div>
  );
}
